import { Link, useNavigate } from 'react-router-dom';
import { api, inr, int, pct, when } from '../../api';
import { Badge, Empty, Figure, Meter, Panel, useAsync } from '../../components';
import { DataTable } from '../../ui/DataTable';

interface Overview {
  quality: { records: number; completeness: string; uniqueness: string; validity: string; consistency: string; timeliness: string; integrity: string; score: string; taken_at: string; details: { articles: number; stockLines: number } } | null;
  openBySeverity: Record<string, number>; byStatus: Record<string, number>; exposureBySeverity: Record<string, number>; reviewQueue: number; familyBlank: { count: number; share: number } | null;
  coverage: { label: string; loaded: boolean; rows: number; last: string | null }[];
  batches: { id: string; system: string; dataset_kind: string; file_name: string; row_count: number; status: string; received_at: string; summary: { duplicateKeys?: number } }[];
}
interface IssueRow { id: number; severity: string; title: string; affected_count: number; financial_exposure: string | null; status: string; rule_name: string }

export function Overview() {
  const nav = useNavigate();
  const ov = useAsync(() => api<Overview>('/api/v1/mdm/overview'), []);
  const top = useAsync(() => api<{ issues: IssueRow[] }>('/api/v1/mdm/issues?status=open&limit=8'), []);
  const d = ov.data;
  const loaded = d?.coverage.filter(c => c.loaded).length ?? 0;
  const open = d ? Object.values(d.openBySeverity).reduce((a, b) => a + b, 0) : 0;
  const exposure = d ? Object.values(d.exposureBySeverity).reduce((a, b) => a + b, 0) : 0;
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="xs">Data intelligence &amp; governance</div>
          <h1>Data quality</h1>
          <p>What LEO CORE can see, how much of it can be trusted, and what needs a decision. Nothing below is shown as a fact unless its source is loaded.</p>
        </div>
        <div className="muted small">Profiled {when(d?.quality?.taken_at)}{d?.coverage.find(c => /product/i.test(c.label))?.last ? ` · product master loaded ${when(d.coverage.find(c => /product/i.test(c.label))!.last)}` : ''}</div>
      </div>

      {ov.error && <div className="callout">Could not reach the API: {ov.error}</div>}

      <section>
        <div className="xs" style={{ marginBottom: 8 }}>What is connected — {loaded} of {d?.coverage.length ?? '…'} sources</div>
        <div className="coverage">
          {d?.coverage.map(c => (
            <div key={c.label} className={`cov ${c.loaded ? 'on' : 'off'}`}>
              <span className="lbl">{c.label}</span>
              <span className="st">{c.loaded ? `${int(c.rows)} rows · ${when(c.last)}` : 'not connected'}</span>
            </div>
          ))}
        </div>
        {d && loaded < d.coverage.length && (
          <div className="callout info" style={{ marginTop: 12 }}>
            Bills, Tally ledgers and the dealer master are not connected, so revenue, receivables and entity-level financials stay <b>unknown</b> — not zero — until they are. The <Link to="/settings/group-structure">group structure</Link> (entities, GSTINs, store models) is in place.
          </div>
        )}
      </section>

      <div className="figures">
        <Figure label="Open findings" value={int(open)} note={d ? `${d.openBySeverity.S1 ?? 0} critical · ${d.openBySeverity.S2 ?? 0} material · ${d.openBySeverity.S3 ?? 0} hygiene` : ''} tone={(d?.openBySeverity.S1 ?? 0) > 0 ? 'bad' : 'warn'} />
        <Figure label="Stock sitting on flagged records" value={inr(exposure)} note="At MRP; findings overlap, so this is an upper bound, not a sum of losses" tone="warn" />
        <Figure label="Awaiting a steward decision" value={int(d?.reviewQueue)} note="Proposed mappings whose evidence conflicts" tone={(d?.reviewQueue ?? 0) > 0 ? 'warn' : 'good'} />
        <Figure label="Products with no family" value={d?.familyBlank ? pct(d.familyBlank.share) : '—'} note={d?.familyBlank ? `${int(d.familyBlank.count)} UPCs — Sub Category blank in WizApp` : 'Profile has not run'} tone={(d?.familyBlank?.share ?? 0) > 0.2 ? 'bad' : 'warn'} />
      </div>

      <div className="grid-2">
        <Panel title="Highest-impact findings" action={<Link to="/settings/data-quality/findings">All findings →</Link>}>
          {top.data?.issues.length ? (
            <DataTable rows={top.data.issues.map(i => ({ ...i, key: String(i.id), exposure: i.financial_exposure != null ? Number(i.financial_exposure) : null }))} exportName="highest-impact-findings" title="Highest-impact findings" filter={false} onRow={i => nav(`/settings/data-quality/findings/${i.id}`)} columns={[
              { key: 'severity', title: 'Severity', kind: 'node', raw: i => i.severity, sortBy: i => i.severity, get: i => <Badge kind={i.severity}>{i.severity}</Badge> },
              { key: 'title', title: 'Finding', kind: 'node', raw: i => `${i.title} (${i.rule_name})`, sortBy: i => i.title, get: i => <>{i.title}<div className="muted small">{i.rule_name}</div></> },
              { key: 'affected_count', title: 'Records', kind: 'units' }, { key: 'exposure', title: 'Stock at MRP', kind: 'money' },
            ]} />
          ) : <Empty title="No findings yet">Run the profiler on a product master to populate this view.</Empty>}
        </Panel>

        <Panel title="Product master quality" action={<span className="muted small">{int(d?.quality?.records)} UPCs · {int(d?.quality?.details.articles)} articles</span>}>
          {d?.quality ? (
            <div style={{ padding: '8px 0' }}>
              <Meter label="Completeness" value={d.quality.completeness} />
              <Meter label="Uniqueness" value={d.quality.uniqueness} />
              <Meter label="Validity" value={d.quality.validity} />
              <Meter label="Consistency" value={d.quality.consistency} />
              <Meter label="Integrity" value={d.quality.integrity} />
              <Meter label="Timeliness" value={d.quality.timeliness} />
              <div className="callout" style={{ margin: '12px 20px 12px' }}>
                The blended score is {pct(d.quality.score, 1)}, but that hides the one structural gap: <b>half of all products have no family</b> (Sub Category blank). The score weights twelve columns equally; the business does not.
              </div>
            </div>
          ) : <Empty title="Not profiled yet" />}
        </Panel>
      </div>

      <Panel title="Recent loads" action={<Link to="/settings/audit">Audit trail →</Link>}>
        {d?.batches.length ? (
          <DataTable rows={d.batches.map(b => ({ ...b, key: b.id, source: `${b.system} · ${b.dataset_kind}` }))} exportName="recent-loads" title="Recent loads" filter={false} columns={[
            { key: 'received_at', title: 'When', kind: 'node', raw: b => b.received_at, sortBy: b => b.received_at, get: b => <span className="num small">{when(b.received_at)}</span> }, { key: 'source', title: 'Source', kind: 'text' },
            { key: 'file_name', title: 'File', kind: 'node', raw: b => b.file_name, sortBy: b => b.file_name, get: b => <span className="small">{b.file_name}{b.summary?.duplicateKeys ? <span className="muted"> · {int(b.summary.duplicateKeys)} in-file duplicates collapsed</span> : null}</span> },
            { key: 'row_count', title: 'Rows', kind: 'units' },
            { key: 'status', title: 'Status', kind: 'node', raw: b => b.status, sortBy: b => b.status, get: b => <Badge kind={b.status === 'validated' || b.status === 'published' ? 'ok' : 'plain'}>{b.status}</Badge> },
          ]} />
        ) : <Empty title="Nothing loaded" />}
      </Panel>
    </div>
  );
}
